<template>
  <div
    class="theme_payment_entry"
    :class="[`theme_payment_entry--${themeId}`, `theme_payment_entry--${layout}`]"
  >
    <section class="theme_payment_entry_hero">
      <div class="theme_payment_entry_brand">
        <div class="theme_payment_entry_logo">
          <img v-if="logoUrl" :src="logoUrl" alt="" />
        </div>
      </div>

      <div class="theme_payment_entry_welcome">
        <strong class="theme_payment_entry_welcome_hi">{{ $t('text_payment_entry_welcome_hi') }}</strong>
        <span class="theme_payment_entry_welcome_service">{{ $t('text_payment_entry_welcome_service') }}</span>
        <p>{{ paymentAccountPopupTitle }}</p>
      </div>

      <img
        v-if="entryHero"
        class="theme_payment_entry_hero_img"
        :src="entryHero"
        alt=""
      />
    </section>

    <section class="theme_payment_entry_body">
      <div class="theme_payment_entry_amount">
        <span>{{ $t('text_payment_amount') }}</span>
        <strong>{{ amountDisplayText }}</strong>
        <img
          v-if="amountDeco"
          class="theme_payment_entry_amount_deco"
          :src="amountDeco"
          alt=""
        />
      </div>

      <div v-if="showOperatorList" class="theme_payment_entry_methods">
        <h2>{{ $t('text_please_select_payment_method') }}</h2>
        <div class="theme_payment_entry_methods_grid">
          <button
            v-for="(item, index) in operatorList"
            :key="item.value || index"
            class="theme_payment_entry_method"
            :class="{ 'is-selected': item.isSelect }"
            type="button"
            @click="$emit('choose-operator', index)"
          >
            <img
              v-if="getPaymentMethodCheckImg(item)"
              class="theme_payment_entry_method_check"
              :src="getPaymentMethodCheckImg(item)"
              alt=""
            />
            <img
              v-if="item.logo"
              class="theme_payment_entry_method_logo"
              :src="item.logo"
              alt=""
            />
            <span
              v-else
              class="theme_payment_entry_method_logo theme_payment_entry_method_logo--fallback"
            />
            <span class="theme_payment_entry_method_name">{{ item.name }}</span>
          </button>
        </div>
      </div>

      <div v-if="needsAccountNumber" class="theme_payment_entry_account">
        <h2 v-if="isCustomGold">{{ $t('text_payment_account_number') }}</h2>
        <div class="theme_payment_entry_input">
          <span class="theme_payment_entry_phone_code">+{{ phoneCode }}</span>
          <input
            :value="value"
            type="text"
            :maxlength="phoneMaxLength"
            :placeholder="paymentAccountPopupInputPlaceholder"
            @input="$emit('input', $event.target.value)"
          />
        </div>
        <div class="theme_payment_entry_warning">
          <img
            v-if="warningIcon"
            class="theme_payment_entry_warning_icon"
            :src="warningIcon"
            alt=""
          />
          <span
            v-else
            class="theme_payment_entry_warning_icon theme_payment_entry_warning_icon--fallback"
          >
            !
          </span>
          <p>
            <template v-if="paymentAccountPopupWarning">{{ paymentAccountPopupWarning }}</template>
            <template v-else>
              {{ $t('text_please_fill_your_payment') }}
              <br />
              {{ $t('text_please_fill_your_payment2') }}
            </template>
          </p>
        </div>
      </div>

      <button class="theme_payment_entry_submit" type="button" @click="$emit('submit')">
        <span>{{ paymentAccountPopupSubmitText }}</span>
        <span v-if="isSelfBlue" class="theme_payment_entry_submit_arrow">&rarr;</span>
      </button>
    </section>
  </div>
</template>

<script>
import paymentAccountPopupCopy from '@/mixins/paymentAccountPopupCopy';
import themeMixin from '@/mixins/theme';

export default {
  name: 'ThemePaymentAccountEntryPage',
  mixins: [themeMixin, paymentAccountPopupCopy],
  props: {
    layout: {
      type: String,
      default: 'moblie',
    },
    value: {
      type: String,
      default: '',
    },
    repaymentData: {
      type: Object,
      default: () => ({}),
    },
    countryInfo: {
      type: Object,
      default: () => ({}),
    },
    countryInfoLogo: {
      type: String,
      default: '',
    },
    operatorList: {
      type: Array,
      default: () => [],
    },
    phoneMaxLength: {
      type: Number,
      default: 10,
    },
    paymentAmount: {
      type: [String, Number],
      default: '',
    },
    showOperatorList: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    isSelfBlue() {
      return this.themeId === 'self-blue';
    },
    isCustomGold() {
      return this.themeId === 'custom-gold';
    },
    logoUrl() {
      return (
        this.repaymentData?.configData?.logoFileUrl ||
        (this.isCustomGold ? this.themeImg('payLogo') : this.countryInfoLogo) ||
        this.themeImg('logo') ||
        this.countryInfoLogo ||
        ''
      );
    },
    entryHero() {
      return this.themeImg('entryHero') || this.themeImg('banner') || '';
    },
    amountDeco() {
      return this.themeImg('amountDeco') || '';
    },
    warningIcon() {
      return this.themeImg('warningIcon') || '';
    },
    paymentMethodSelectedIcon() {
      return this.themeImg('paymentSelected') || this.paymentMethodSelectedImg || '';
    },
    paymentMethodUnselectedIcon() {
      return this.themeImg('paymentUnselected') || '';
    },
    currencyCode() {
      return this.countryInfo?.currencyCode || '';
    },
    amountDisplayText() {
      const amount = this.paymentAmount || '';
      if (this.isSelfBlue && this.currencyCode) {
        return `${this.currencyCode} ${amount}`;
      }
      return `${this.currencyCode}${amount}`;
    },
    phoneCode() {
      return this.countryInfo?.phoneCode || '';
    },
    hasOperatorList() {
      return this.showOperatorList;
    },
    needsAccountNumber() {
      return this.repaymentData?.extraParams?.includes('customerAccountNumber');
    },
  },
  methods: {
    getPaymentMethodCheckImg(item) {
      return item?.isSelect
        ? this.paymentMethodSelectedIcon
        : this.paymentMethodUnselectedIcon;
    },
  },
};
</script>

<style lang="scss" scoped>
.theme_payment_entry {
  min-height: 100vh;
  width: 100%;
  max-width: 375px;
  margin: 0 auto;
  background: var(--pay-color-bg-page);
  color: var(--pay-color-text-primary);
  box-sizing: border-box;
  overflow-x: hidden;
}

.theme_payment_entry--computer {
  max-width: 640px;
}

.theme_payment_entry_hero {
  position: relative;
  min-height: 231px;
  padding: 36px 30px 18px;
  box-sizing: border-box;
  overflow: hidden;
}

.theme_payment_entry--custom-gold .theme_payment_entry_hero {
  height: 268px;
  min-height: 268px;
  padding: 26px 24px 58px;
  color: #fff;
  background: linear-gradient(-23deg, #392812 0.6%, #171716 79.5%);
}

.theme_payment_entry_brand {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 12px;
}

.theme_payment_entry_logo {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: auto;
  height: 44px;
  border-radius: 0;
  background: transparent;
  overflow: visible;
}

.theme_payment_entry--self-blue .theme_payment_entry_logo {
  margin-left: 6px;
}

.theme_payment_entry--custom-gold .theme_payment_entry_logo {
  width: auto;
  height: 48px;
  border-radius: 0;
  background: transparent;
}

.theme_payment_entry_logo img {
  display: block;
  width: auto;
  height: 100%;
  max-width: none;
  max-height: 100%;
  object-fit: contain;
}

.theme_payment_entry_logo span {
  font-size: 12px;
  font-weight: 700;
  color: var(--pay-color-primary);
}

.theme_payment_entry_welcome {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 28px;
  color: #656a73;
}

.theme_payment_entry_welcome_hi,
.theme_payment_entry_welcome_service {
  display: block;
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.25;
  color: currentColor;
}

.theme_payment_entry--self-blue .theme_payment_entry_welcome {
  margin-top: 27px;
  max-width: 165px;
  color: #656a73;
}

.theme_payment_entry--custom-gold .theme_payment_entry_welcome {
  margin-top: 27px;
  max-width: 216px;
  color: #fff;
}

.theme_payment_entry--computer .theme_payment_entry_welcome {
  max-width: min(560px, 100%);
}

.theme_payment_entry_welcome p {
  margin: 4px 0 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.18;
  color: currentColor;
}

.theme_payment_entry--custom-gold .theme_payment_entry_welcome p {
  font-size: 19px;
}

.theme_payment_entry--computer .theme_payment_entry_welcome p {
  width: auto;
  max-width: 100%;
  height: auto;
  margin-top: 34px;
  font-size: 13px;
  font-weight: 500;
  line-height: 15px;
}

.theme_payment_entry--self-blue.theme_payment_entry--moblie .theme_payment_entry_welcome,
.theme_payment_entry--custom-gold.theme_payment_entry--moblie .theme_payment_entry_welcome {
  max-width: 313px;
}

.theme_payment_entry--self-blue.theme_payment_entry--moblie .theme_payment_entry_welcome p,
.theme_payment_entry--custom-gold.theme_payment_entry--moblie .theme_payment_entry_welcome p {
  width: 313px;
  max-width: 100%;
  height: 30px;
  margin-top: 20px;
  font-family: SFPro, SFPro, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 15px;
  text-align: left;
  font-style: normal;
}

.theme_payment_entry--self-blue.theme_payment_entry--moblie .theme_payment_entry_welcome p {
  color: #0C2B59;
}

.theme_payment_entry--custom-gold.theme_payment_entry--moblie .theme_payment_entry_welcome p {
  color: #FFFFFF;
}

.theme_payment_entry_intro {
  margin: 0;
}

.theme_payment_entry_intro {
  position: relative;
  z-index: 2;
  margin-top: 18px;
  max-width: 320px;
  font-size: 13px;
  line-height: 1.45;
}

.theme_payment_entry--self-blue .theme_payment_entry_intro {
  margin-top: 22px;
  max-width: 326px;
  color: var(--pay-color-primary-navy);
}

.theme_payment_entry--custom-gold .theme_payment_entry_intro {
  margin-top: 16px;
  max-width: 327px;
  color: #fff;
}

.theme_payment_entry_hero_img {
  position: absolute;
  z-index: 1;
  object-fit: contain;
  pointer-events: none;
}

.theme_payment_entry--self-blue .theme_payment_entry_hero_img {
  right: -5px;
  top: 37px;
  width: 182px;
  height: 130px;
  object-fit: contain;
  object-position: center;
}

.theme_payment_entry--custom-gold .theme_payment_entry_hero_img {
  right: -2px;
  top: 66px;
  width: 132px;
  height: 98px;
}

.theme_payment_entry_body {
  padding: 0 20px 20px;
  box-sizing: border-box;
}

.theme_payment_entry--custom-gold .theme_payment_entry_body {
  margin-top: -36px;
  position: relative;
  z-index: 3;
}

.theme_payment_entry_amount {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 76px;
  padding: 17px 19px;
  box-sizing: border-box;
  border-radius: 8px;
  background: var(--pay-color-bg-card);
  box-shadow: 0 4px 16px var(--pay-color-bg-overlay);
  overflow: hidden;
}

.theme_payment_entry--self-blue .theme_payment_entry_amount {
  display: block;
  min-height: 70px;
  padding: 14px 23px;
  border-radius: 12px;
  background: var(--pay-color-primary);
  box-shadow: none;
}

.theme_payment_entry_amount span {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  color: var(--pay-color-text-brown-light);
}

.theme_payment_entry--self-blue .theme_payment_entry_amount span {
  display: block;
  max-width: 180px;
  color: var(--pay-color-text-blue);
}

.theme_payment_entry_amount strong {
  flex: 0 0 auto;
  font-size: 26px;
  font-weight: 600;
  color: var(--pay-color-amount-red);
}

.theme_payment_entry--self-blue .theme_payment_entry_amount strong {
  display: block;
  margin-top: 1px;
  max-width: 180px;
  font-size: 18px;
  line-height: 1.2;
  overflow-wrap: break-word;
  word-break: normal;
}

.theme_payment_entry--self-blue.theme_payment_entry--computer .theme_payment_entry_amount span,
.theme_payment_entry--self-blue.theme_payment_entry--computer .theme_payment_entry_amount strong {
  max-width: calc(100% - 80px);
}

.theme_payment_entry_amount_deco {
  display: none;
}

.theme_payment_entry--self-blue .theme_payment_entry_amount_deco {
  position: absolute;
  top: 10px;
  right: 11px;
  display: block;
  width: 56px;
  height: 56px;
  object-fit: contain;
  pointer-events: none;
}

.theme_payment_entry_methods {
  margin-top: 21px;
}

.theme_payment_entry--self-blue .theme_payment_entry_methods {
  margin-top: 23px;
}

.theme_payment_entry_methods h2,
.theme_payment_entry_account h2 {
  margin: 0 0 10px;
  width: 100%;
  max-width: 100%;
  font-size: 15px;
  font-weight: 600;
  line-height: 18px;
  color: var(--pay-color-text-primary);
}

.theme_payment_entry--custom-gold .theme_payment_entry_methods h2 {
  margin-bottom: 6px;
}

.theme_payment_entry_methods_grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 13px;
}

.theme_payment_entry_method {
  position: relative;
  min-width: 0;
  height: 118px;
  padding: 22px 8px 12px;
  border: 1px solid var(--pay-color-border-lighter);
  border-radius: 8px;
  background: var(--pay-color-bg-card);
  box-shadow: none;
  cursor: pointer;
  box-sizing: border-box;
}

.theme_payment_entry--self-blue .theme_payment_entry_method {
  height: 110px;
  padding-top: 19px;
  border-color: var(--pay-color-border-gray);
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
}

.theme_payment_entry_method.is-selected {
  border-color: var(--pay-color-border-accent);
  background: #fff1e0;
  box-shadow: 0 2px 8px rgba(103, 64, 0, 0.15);
}

.theme_payment_entry--self-blue .theme_payment_entry_method.is-selected {
  border-color: var(--pay-color-border-gold);
  background: var(--pay-color-bg-secondary);
}

.theme_payment_entry_method_check {
  position: absolute;
  top: 7px;
  right: 7px;
  width: 18px;
  height: 18px;
  display: block;
  object-fit: contain;
  pointer-events: none;
}

.theme_payment_entry--self-blue .theme_payment_entry_method_check {
  top: 8px;
  right: 8px;
  width: 16px;
  height: 16px;
}

.theme_payment_entry_method_logo {
  display: block;
  width: 52px;
  height: 52px;
  margin: 0 auto;
  border-radius: 50%;
  object-fit: contain;
  overflow: hidden;
}

.theme_payment_entry_method_logo--fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--pay-color-primary-light) 55%, #fff);
  color: var(--pay-color-text-brown);
  overflow: hidden;
  font-size: 11px;
}

.theme_payment_entry--self-blue .theme_payment_entry_method_logo--fallback {
  background: #4b6b9b;
  color: var(--pay-color-text-blue-light);
}

.theme_payment_entry--custom-gold .theme_payment_entry_method_logo--fallback {
  background: #fff7ed;
  color: var(--pay-color-text-brown);
}

.theme_payment_entry--custom-gold .theme_payment_entry_method.is-selected .theme_payment_entry_method_logo--fallback {
  background: #fcb862;
}

.theme_payment_entry_method_name {
  display: block;
  margin-top: 8px;
  font-size: 13px;
  font-weight: 600;
  color: var(--pay-color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.theme_payment_entry_account {
  margin-top: 20px;
  padding: 16px;
  border: 1px solid var(--pay-color-border-lighter);
  border-radius: 8px;
  background: var(--pay-color-bg-card);
  box-sizing: border-box;
}

.theme_payment_entry--self-blue .theme_payment_entry_account {
  margin-top: 24px;
  padding: 0;
  border: none;
  background: transparent;
}

.theme_payment_entry--custom-gold .theme_payment_entry_account {
  min-height: 216px;
  margin-top: 23px;
  padding: 17px;
  border-color: #feeed6;
}

.theme_payment_entry_input {
  display: flex;
  align-items: stretch;
  height: 52px;
  border-radius: 8px;
  background: var(--pay-color-bg-stripe);
  overflow: hidden;
}

.theme_payment_entry--self-blue .theme_payment_entry_input {
  height: 50px;
  border: 1px solid var(--pay-color-border-input);
  border-radius: 16px;
  background: var(--pay-color-bg-card);
}

.theme_payment_entry_phone_code {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 62px;
  color: var(--pay-color-text-secondary);
  font-size: 15px;
  font-weight: 600;
}

.theme_payment_entry_input input {
  min-width: 0;
  flex: 1;
  border: 1px solid var(--pay-color-border-input);
  border-radius: 0 8px 8px 0;
  background: var(--pay-color-bg-input);
  padding: 0 12px;
  color: var(--pay-color-text-primary);
  font-size: 15px;
  outline: none;
}

.theme_payment_entry--self-blue .theme_payment_entry_input input {
  border-top: 0;
  border-right: 0;
  border-bottom: 0;
  border-radius: 0;
}

.theme_payment_entry--custom-gold .theme_payment_entry_account h2 {
  margin-bottom: 12px;
  color: var(--pay-color-text-primary);
}

.theme_payment_entry--custom-gold .theme_payment_entry_input input {
  padding: 0 10px;
  font-size: 14px;
}

.theme_payment_entry--custom-gold .theme_payment_entry_input input::placeholder {
  font-size: 10px;
}

.theme_payment_entry_input input::placeholder {
  color: var(--pay-color-text-placeholder);
}

.theme_payment_entry_warning {
  display: flex;
  gap: 10px;
  margin-top: 12px;
  padding: 13px 16px;
  border-radius: 10px;
  background: var(--pay-color-danger-bg);
  color: var(--pay-color-danger);
  box-sizing: border-box;
}

.theme_payment_entry--self-blue .theme_payment_entry_warning {
  margin-top: 18px;
  min-height: 75px;
  border: 1px solid var(--pay-color-danger-border);
  border-radius: 12px;
}

.theme_payment_entry--custom-gold .theme_payment_entry_warning {
  margin-top: 10px;
  min-height: 86px;
  padding: 12px 16px;
  border-radius: 8px;
}

.theme_payment_entry_warning_icon {
  flex: 0 0 20px;
  width: 20px;
  height: 20px;
  display: block;
  object-fit: contain;
}

.theme_payment_entry_warning_icon--fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--pay-color-danger);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
}

.theme_payment_entry_warning p {
  min-width: 0;
  flex: 1;
  margin: 0;
  font-size: 12px;
  line-height: 1.35;
  overflow-wrap: break-word;
  word-break: normal;
}

.theme_payment_entry_submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 50px;
  margin-top: 16px;
  border: 0;
  border-radius: 8px;
  background: var(--pay-gradient-btn-primary);
  color: var(--pay-color-btn-text);
  font-size: 15px;
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  cursor: pointer;
}

.theme_payment_entry--self-blue .theme_payment_entry_submit {
  height: 49px;
  margin-top: 21px;
  border-radius: 12px;
}

.theme_payment_entry--custom-gold .theme_payment_entry_submit {
  height: 52px;
  margin-top: 14px;
}

.theme_payment_entry_submit_arrow {
  font-size: 18px;
  line-height: 1;
}

@supports not (background: color-mix(in srgb, #000 50%, #fff)) {
  .theme_payment_entry_method_logo--fallback {
    background: var(--pay-color-bg-secondary);
  }
}
</style>
